// ═══════════════════════════════════════════════════════════════
// VolunteerOS — Public Volunteer Signup
// Public-facing page for volunteer registration & event sign-up
// Route: /volunteer/signup  or  /volunteer/signup/:incidentId
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Paper, Text, TextInput, Select, Button,
  Group, Stack, SimpleGrid, Badge, Checkbox, ScrollArea,
  Stepper, Loader, Divider,
} from '../../components/gab/GabComponents';
import {
  IconHeartHandshake, IconUser, IconPhone, IconMail, IconMapPin,
  IconShieldCheck, IconCalendarEvent, IconCheck, IconChevronLeft,
  IconChevronRight, IconSend, IconAlertCircle, IconLogin, IconUserPlus,
  IconEdit, IconList,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { fetchTable, createRecord, updateRecord, deleteRecord, TABLES, FIELDS } from '../../lib/api-switch';
import { queryRecords, volunteerLogin, volunteerRegister, volunteerForgotPassword, volunteerResetPassword } from '../../api/client';
import { secondary } from '../../lib/colors';
import '../../styles/public-form.css';

// ── Constants ─────────────────────────────────────────────────

const VOLUNTEER_SESSION_KEY = 'volunteer_session';

const CERT_OPTIONS = [
  'CPR/AED', 'First Aid', 'CERT', 'ICS 100', 'ICS 200', 'ICS 300',
  'ICS 700', 'Ham Radio', 'Chainsaw', 'Swift Water', 'Wilderness FA',
];
const SKILL_OPTIONS = [
  'Medical', 'Communications', 'Logistics', 'Search & Rescue',
  'Heavy Equipment', 'Counseling', 'Translation', 'Cooking', 'Construction', 'IT/Tech',
];
const EQUIPMENT_OPTIONS = [
  'Chainsaw', 'Generator', 'Water Pump', 'ATV', 'Boat', 'Trailer', 'Ham Radio', 'Medical Bag',
];
const ROLE_OPTIONS = [
  { value: 'active', label: 'Field Operations' },
  { value: 'office', label: 'Admin / Office' },
  { value: 'medical', label: 'Medical' },
  { value: 'comms', label: 'Communications' },
  { value: 'both', label: 'Flexible / Any' },
];
const VEHICLE_OPTIONS = [
  { value: 'no', label: 'No Vehicle' },
  { value: 'personal', label: 'Personal Vehicle' },
  { value: 'transport', label: 'Can Transport Others' },
];
const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const LANGUAGE_OPTIONS = [
  { value: 'English', label: 'English' }, { value: 'Spanish', label: 'Spanish' },
  { value: 'French', label: 'French' }, { value: 'Mandarin', label: 'Mandarin' },
  { value: 'Cantonese', label: 'Cantonese' }, { value: 'Vietnamese', label: 'Vietnamese' },
  { value: 'Tagalog', label: 'Tagalog' }, { value: 'Arabic', label: 'Arabic' },
  { value: 'Korean', label: 'Korean' }, { value: 'Russian', label: 'Russian' },
  { value: 'Haitian Creole', label: 'Haitian Creole' }, { value: 'Portuguese', label: 'Portuguese' },
  { value: 'Hindi', label: 'Hindi' }, { value: 'Somali', label: 'Somali' },
  { value: 'Other', label: 'Other' },
];
const DAYS    = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PERIODS = [
  { key: 'morning', label: 'Morning (6a-12p)' }, { key: 'afternoon', label: 'Afternoon (12-6p)' },
  { key: 'evening', label: 'Evening (6p-12a)' }, { key: 'overnight', label: 'Overnight (12-6a)' },
];
const WAIVER_TEXT = `VOLUNTEER LIABILITY WAIVER AND RELEASE OF LIABILITY

1. ASSUMPTION OF RISK: I understand that volunteering in emergency management and disaster response activities involves inherent risks including but not limited to physical injury, illness, exposure to hazardous conditions, property damage, disability, or death.

2. RELEASE AND WAIVER OF LIABILITY: I hereby release, waive, discharge, and covenant not to sue the organization, its officers, directors, employees, agents, volunteers, sponsors, and affiliates from any and all liability for any loss, damage, injury, or expense that I may suffer as a result of my participation in volunteer activities.

3. INDEMNIFICATION: I agree to indemnify and hold harmless the organization from any claims, damages, or expenses arising from my participation in volunteer activities.

4. MEDICAL AUTHORIZATION: In the event of an emergency, I authorize the organization to obtain emergency medical treatment on my behalf. I understand that the organization does not provide health insurance coverage for volunteers.

5. PHOTO AND MEDIA RELEASE: I grant the organization permission to use my name, likeness, image, and voice in any and all media for promotional, educational, and informational purposes.

6. GOVERNING LAW: This agreement shall be governed by and construed in accordance with the laws of the state in which volunteer activities are conducted.

7. ACKNOWLEDGEMENT: I have read this Volunteer Liability Waiver and Release of Liability, fully understand its terms, and understand that I am giving up substantial rights by signing it. I acknowledge that I am signing this agreement freely and voluntarily.`;

const TYPE_COLORS: Record<string, string> = {
  distribution: '#03AC9E', mucking: '#F86A0B', medical: 'var(--color-error)',
  intake: '#03AC9E', command: 'var(--data-viz-650)', shelter: '#C68700', other: '#939598',
};

const STATUS_COLORS: Record<string, string> = {
  confirmed: '#03AC9E', attended: '#4B3FFF', completed: '#4B3FFF',
  cancelled: '#D33423', pending: '#C68700',
};

// ── Types ─────────────────────────────────────────────────────

interface VolunteerSession {
  id: string; email: string; firstName: string; lastName: string; fullName: string;
}
interface PublicEvent {
  id: number; incidentId: string; name: string; type: string; status: string;
  location: string; address: string; startDate: string; endDate: string;
  targetVol: number; checkInCode: string; notes: string;
}
interface PublicIncident {
  id: number; name: string; status: string; incidentType: string;
}
interface MyAssignment {
  assignmentId: number; eventId: number; eventName: string;
  location: string; startDate: string; status: string; role: string;
}

// ── Helpers ───────────────────────────────────────────────────

function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
function saveSession(s: VolunteerSession) {
  try { sessionStorage.setItem(VOLUNTEER_SESSION_KEY, JSON.stringify(s)); } catch {}
}
function loadSession(): VolunteerSession | null {
  try { const r = sessionStorage.getItem(VOLUNTEER_SESSION_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
function clearSession() {
  try { sessionStorage.removeItem(VOLUNTEER_SESSION_KEY); } catch {}
}

// ── Auth Panel ─────────────────────────────────────────────────

interface AuthPanelProps {
  session: VolunteerSession | null;
  onAuth: (s: VolunteerSession) => void;
  onRegister: (s: VolunteerSession) => void;
  onGuest: () => void;
  onSignOut: () => void;
}

function AuthPanel({ session, onAuth, onRegister, onGuest, onSignOut }: AuthPanelProps) {
  const [mode, setMode]           = useState<'prompt' | 'signin' | 'register' | 'forgot' | 'reset'>('prompt');
  const [authFirst, setAuthFirst] = useState('');
  const [authLast, setAuthLast]   = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPw, setAuthPw]       = useState('');
  const [authConf, setAuthConf]   = useState('');
  const [resetCode, setResetCode] = useState('');
  const [authErr, setAuthErr]     = useState('');
  const [authMsg, setAuthMsg]     = useState('');
  const [loading, setLoading]     = useState(false);

  const doSignIn = async () => {
    if (!authEmail.trim() || !authPw) { setAuthErr('Email and password are required.'); return; }
    setAuthErr(''); setLoading(true);
    try {
      const user = await volunteerLogin(authEmail.trim(), authPw);
      saveSession(user); onAuth(user);
    } catch (err) {
      setAuthErr(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  const doRegister = async () => {
    if (!authFirst.trim() || !authLast.trim() || !authEmail.trim() || !authPw) {
      setAuthErr('All fields are required.'); return;
    }
    if (authPw !== authConf) { setAuthErr('Passwords do not match.'); return; }
    if (authPw.length < 8)   { setAuthErr('Password must be at least 8 characters.'); return; }
    setAuthErr(''); setLoading(true);
    try {
      const user = await volunteerRegister(authEmail.trim(), authPw, authFirst.trim(), authLast.trim());
      saveSession(user); onRegister(user);
    } catch (err) {
      setAuthErr(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  const back = () => { setMode('prompt'); setAuthErr(''); setAuthMsg(''); };

  const doForgot = async () => {
    if (!authEmail.trim()) { setAuthErr('Enter your email address.'); return; }
    setAuthErr(''); setLoading(true);
    try {
      await volunteerForgotPassword(authEmail.trim());
      setAuthMsg('Check your email for a reset code, then enter it below.');
      setMode('reset');
    } catch (err) {
      setAuthErr(err instanceof Error ? err.message : 'Request failed.');
    } finally { setLoading(false); }
  };

  const doReset = async () => {
    if (!authEmail.trim() || !resetCode.trim() || !authPw) {
      setAuthErr('All fields are required.'); return;
    }
    if (authPw !== authConf) { setAuthErr('Passwords do not match.'); return; }
    if (authPw.length < 8)   { setAuthErr('Password must be at least 8 characters.'); return; }
    setAuthErr(''); setLoading(true);
    try {
      await volunteerResetPassword(authEmail.trim(), resetCode.trim(), authPw);
      setAuthMsg('Password reset! You can now sign in.');
      setAuthPw(''); setAuthConf(''); setResetCode('');
      setMode('signin');
    } catch (err) {
      setAuthErr(err instanceof Error ? err.message : 'Reset failed. Check your code and try again.');
    } finally { setLoading(false); }
  };

  if (session) {
    const initials = `${session.firstName[0] ?? ''}${session.lastName[0] ?? ''}`.toUpperCase();
    return (
      <Paper p="md" radius="sm" withBorder mb="md" style={{ borderColor: '#03AC9E', backgroundColor: '#f0fffe' }}>
        <Group justify="space-between">
          <Group gap="sm">
            <div style={{
              width: 38, height: 38, borderRadius: '50%', backgroundColor: '#03AC9E',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: 14, flexShrink: 0,
            }}>{initials}</div>
            <div>
              <Text fw={600} size="sm" style={{ color: '#037a6e' }}>
                Signed in as {session.fullName || `${session.firstName} ${session.lastName}`}
              </Text>
              <Text size="xs" c="dimmed">{session.email}</Text>
            </div>
          </Group>
          <Button variant="subtle" size="xs" color="gray" onClick={onSignOut}>Sign out</Button>
        </Group>
      </Paper>
    );
  }

  if (mode === 'prompt') {
    return (
      <Paper p="md" radius="sm" withBorder mb="md">
        <Group gap="sm" mb="sm" align="flex-start">
          <div style={{
            width: 40, height: 40, borderRadius: '50%', backgroundColor: '#f0fffe',
            border: '1px solid #99f6e4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <IconUser size={20} color="#03AC9E" />
          </div>
          <div>
            <Text fw={600} size="sm">Sign in or create an account</Text>
            <Text size="xs" c="dimmed">
              New volunteers: create an account to complete your profile once — then sign up for future events in seconds.
            </Text>
          </div>
        </Group>
        <Group gap="xs">
          <Button size="sm" color="#03AC9E" leftSection={<IconLogin size={14} />} onClick={() => setMode('signin')}>Sign In</Button>
          <Button size="sm" variant="light" color="#03AC9E" leftSection={<IconUserPlus size={14} />} onClick={() => setMode('register')}>Create Account</Button>
          <Button size="sm" variant="subtle" color="gray" style={{ marginLeft: 'auto' }} onClick={onGuest}>Continue as guest</Button>
        </Group>
      </Paper>
    );
  }

  if (mode === 'signin') {
    return (
      <Paper p="md" radius="sm" withBorder mb="md" style={{ borderColor: '#03AC9E' }}>
        <Group justify="space-between" mb="sm">
          <Text fw={600} size="sm">Sign In to Your Volunteer Account</Text>
          <Button variant="subtle" size="xs" color="gray" onClick={back}>← Back</Button>
        </Group>
        {authMsg && <Text size="xs" style={{ color: '#037a6e', marginBottom: 8 }}>{authMsg}</Text>}
        <Stack gap="sm">
          <TextInput label="Email" type="email" value={authEmail} autoComplete="email"
            onChange={e => setAuthEmail(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doSignIn(); }} />
          <div>
            <TextInput label="Password" type="password" value={authPw} autoComplete="current-password"
              onChange={e => setAuthPw(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doSignIn(); }} />
            <div style={{ textAlign: 'right', marginTop: 4 }}>
              <button type="button" style={{ background: 'none', border: 'none', color: '#03AC9E', cursor: 'pointer', fontSize: 12, padding: 0 }}
                onClick={() => { setAuthErr(''); setAuthMsg(''); setMode('forgot'); }}>Forgot password?</button>
            </div>
          </div>
        </Stack>
        {authErr && <Text size="xs" style={{ color: 'var(--color-error)', marginTop: 6 }}>{authErr}</Text>}
        <Group mt="sm" gap="sm" align="center">
          <Button size="sm" color="#03AC9E" loading={loading} onClick={doSignIn}>Sign In</Button>
          <Text size="xs" c="dimmed">No account?{' '}
            <button type="button" style={{ background: 'none', border: 'none', color: '#03AC9E', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 'inherit' }}
              onClick={() => { setMode('register'); setAuthErr(''); setAuthMsg(''); }}>Create one</button>
          </Text>
        </Group>
      </Paper>
    );
  }

  if (mode === 'forgot') {
    return (
      <Paper p="md" radius="sm" withBorder mb="md" style={{ borderColor: '#03AC9E' }}>
        <Group justify="space-between" mb="sm">
          <Text fw={600} size="sm">Reset Your Password</Text>
          <Button variant="subtle" size="xs" color="gray" onClick={() => { setMode('signin'); setAuthErr(''); setAuthMsg(''); }}>← Back</Button>
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          Enter your email address and we&apos;ll send you a code to reset your password.
        </Text>
        <TextInput label="Email" type="email" value={authEmail} autoComplete="email"
          onChange={e => setAuthEmail(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doForgot(); }} />
        {authErr && <Text size="xs" style={{ color: 'var(--color-error)', marginTop: 6 }}>{authErr}</Text>}
        <Group mt="sm" gap="sm" align="center">
          <Button size="sm" color="#03AC9E" loading={loading} onClick={doForgot}>Send Reset Code</Button>
          <Text size="xs" c="dimmed">Already have a code?{' '}
            <button type="button" style={{ background: 'none', border: 'none', color: '#03AC9E', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 'inherit' }}
              onClick={() => { setMode('reset'); setAuthErr(''); }}>Enter it here</button>
          </Text>
        </Group>
      </Paper>
    );
  }

  if (mode === 'reset') {
    return (
      <Paper p="md" radius="sm" withBorder mb="md" style={{ borderColor: '#03AC9E' }}>
        <Group justify="space-between" mb="sm">
          <Text fw={600} size="sm">Enter Your Reset Code</Text>
          <Button variant="subtle" size="xs" color="gray" onClick={() => { setMode('signin'); setAuthErr(''); setAuthMsg(''); }}>← Back</Button>
        </Group>
        {authMsg && <Text size="xs" style={{ color: '#037a6e', marginBottom: 8 }}>{authMsg}</Text>}
        <Stack gap="sm">
          <TextInput label="Email" type="email" value={authEmail} autoComplete="email"
            onChange={e => setAuthEmail(e.target.value)} />
          <TextInput label="Reset Code" placeholder="Code from your email" value={resetCode}
            onChange={e => setResetCode(e.target.value)} />
          <TextInput label="New Password" type="password" value={authPw} autoComplete="new-password"
            description="Minimum 8 characters" onChange={e => setAuthPw(e.target.value)} />
          <TextInput label="Confirm New Password" type="password" value={authConf} autoComplete="new-password"
            onChange={e => setAuthConf(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doReset(); }} />
        </Stack>
        {authErr && <Text size="xs" style={{ color: 'var(--color-error)', marginTop: 6 }}>{authErr}</Text>}
        <Button mt="sm" size="sm" color="#03AC9E" loading={loading} onClick={doReset}>Reset Password</Button>
      </Paper>
    );
  }

  return (
    <Paper p="md" radius="sm" withBorder mb="md" style={{ borderColor: '#03AC9E' }}>
      <Group justify="space-between" mb="xs">
        <Text fw={600} size="sm">Create a Volunteer Account</Text>
        <Button variant="subtle" size="xs" color="gray" onClick={back}>← Back</Button>
      </Group>
      <Text size="xs" c="dimmed" mb="sm">
        After creating your account you&apos;ll complete your volunteer profile once — future sign-ups only take a click.
      </Text>
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput label="First Name" required value={authFirst} autoComplete="given-name"
            onChange={e => setAuthFirst(e.target.value)} />
          <TextInput label="Last Name" required value={authLast} autoComplete="family-name"
            onChange={e => setAuthLast(e.target.value)} />
        </SimpleGrid>
        <TextInput label="Email" type="email" required value={authEmail} autoComplete="email"
          onChange={e => setAuthEmail(e.target.value)} />
        <TextInput label="Password" type="password" required value={authPw} autoComplete="new-password"
          description="Minimum 8 characters" onChange={e => setAuthPw(e.target.value)} />
        <TextInput label="Confirm Password" type="password" required value={authConf} autoComplete="new-password"
          onChange={e => setAuthConf(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doRegister(); }} />
      </Stack>
      {authErr && <Text size="xs" style={{ color: 'var(--color-error)', marginTop: 6 }}>{authErr}</Text>}
      <Group mt="sm" gap="sm" align="center">
        <Button size="sm" color="#03AC9E" loading={loading} onClick={doRegister}>
          Create Account &amp; Set Up Profile
        </Button>
        <Text size="xs" c="dimmed">Already have one?{' '}
          <button type="button" style={{ background: 'none', border: 'none', color: '#03AC9E', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 'inherit' }}
            onClick={() => { setMode('signin'); setAuthErr(''); }}>Sign in</button>
        </Text>
      </Group>
    </Paper>
  );
}

// ── Main Component ─────────────────────────────────────────────

const STEP_CONFIRM = 99;

export default function PublicSignup() {
  const { incidentId } = useParams<{ incidentId?: string }>();

  // Auth & profile
  const [session, setSession]                     = useState<VolunteerSession | null>(null);
  const [volunteerRecordId, setVolunteerRecordId] = useState<number | null>(null);
  const [registeredEventIds, setRegisteredEventIds] = useState<Set<number>>(new Set());
  const [myAssignments, setMyAssignments]         = useState<MyAssignment[]>([]);
  const [prefillLoading, setPrefillLoading]       = useState(false);

  // Data
  const [events, setEvents]       = useState<PublicEvent[]>([]);
  const [incidents, setIncidents] = useState<PublicIncident[]>([]);
  const [loading, setLoading]     = useState(true);

  // Flow
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [step, setStep]         = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false); // only for guests
  const [eventsTab, setEventsTab]   = useState<'available' | 'mine'>('available');

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]         = useState('');
  const [phone, setPhone]         = useState('');
  const [address, setAddress]     = useState('');
  const [city, setCity]           = useState('');
  const [state, setState]         = useState('');
  const [zip, setZip]             = useState('');
  const [dob, setDob]             = useState('');
  const [shirtSize, setShirtSize] = useState<string | null>(null);
  const [dietary, setDietary]     = useState('');
  const [ecName, setEcName]       = useState('');
  const [ecPhone, setEcPhone]     = useState('');
  const [ecRelationship, setEcRelationship] = useState('');
  const [certs, setCerts]         = useState<string[]>([]);
  const [skills, setSkills]       = useState<string[]>([]);
  const [equipment, setEquipment] = useState<string[]>([]);
  const [rolePref, setRolePref]   = useState<string | null>('both');
  const [vehicle, setVehicle]     = useState<string | null>('no');
  const [language, setLanguage]   = useState<string | null>(null);
  const [languageOther, setLanguageOther] = useState('');
  const [availability, setAvailability]   = useState<Record<string, Record<string, boolean>>>({});
  const [waiverAccepted, setWaiverAccepted] = useState(false);

  // ── Load assignments for a volunteer record ───────────────

  const loadAssignments = useCallback(async (rid: number, allEvents: PublicEvent[]) => {
    try {
      const AF = FIELDS.volunteerAssignments;
      const res = await queryRecords<Record<string, unknown>>(TABLES.volunteerAssignments, {
        filters: [{ field: AF.volunteerId, operator: 'eq', value: String(rid) }],
        limit: 200,
      }, { anonymous: true });

      const ids = new Set<number>();
      const assignments: MyAssignment[] = [];

      for (const r of res.records) {
        const evId = Number(r[AF.eventId] || 0);
        if (evId) ids.add(evId);

        // Try to get event name/location/date from events array, fall back to notes
        const matchedEvent = allEvents.find(e => e.id === evId);
        let eventName = matchedEvent?.name || '';
        let location  = matchedEvent?.location || '';
        let startDate = matchedEvent?.startDate || '';
        if (!eventName) {
          try {
            const n = JSON.parse(String(r[AF.notes] || '{}'));
            eventName = n.eventName || `Event #${evId}`;
          } catch { eventName = `Event #${evId}`; }
        }

        assignments.push({
          assignmentId: Number(r.record_id || r.id || 0),
          eventId:      evId,
          eventName,
          location,
          startDate,
          status:  String(r[AF.status]   || 'confirmed'),
          role:    String(r[AF.role]     || ''),
        });
      }

      setRegisteredEventIds(ids);
      setMyAssignments(assignments.sort((a, b) => a.eventName.localeCompare(b.eventName)));
    } catch (err) {
      console.warn('Could not load assignments:', err);
    }
  }, []);

  // ── Pre-fill form from volunteer record ───────────────────

  const prefillFromSession = useCallback(async (s: VolunteerSession, allEvents: PublicEvent[]) => {
    setPrefillLoading(true);
    try {
      const F = FIELDS.volunteers;
      const res = await queryRecords<Record<string, unknown>>(TABLES.volunteers, {
        filters: [{ field: F.email, operator: 'eq', value: s.email }],
        limit: 1,
      }, { anonymous: true });

      setFirstName(s.firstName || '');
      setLastName(s.lastName   || '');
      setEmail(s.email         || '');

      if (res.records.length === 0) return;

      const r   = res.records[0];
      const rid = Number(r.record_id || r.id || 0);
      if (rid) {
        setVolunteerRecordId(rid);
        loadAssignments(rid, allEvents);
      }

      if (r[F.firstName])  setFirstName(String(r[F.firstName]));
      if (r[F.lastName])   setLastName(String(r[F.lastName]));
      if (r[F.email])      setEmail(String(r[F.email]));
      if (r[F.phone])      setPhone(String(r[F.phone]));
      if (r[F.address])    setAddress(String(r[F.address]));
      if (r[F.city])       setCity(String(r[F.city]));
      if (r[F.state])      setState(String(r[F.state]));
      if (r[F.zip])        setZip(String(r[F.zip]));
      if (r[F.emergencyContact]) setEcName(String(r[F.emergencyContact]));
      if (r[F.emergencyPhone])   setEcPhone(String(r[F.emergencyPhone]));

      const certsStr = String(r[F.certifications] || '');
      if (certsStr) setCerts(certsStr.split(',').map(c => c.trim()).filter(Boolean));
      const skillsStr = String(r[F.skills] || '');
      if (skillsStr) setSkills(skillsStr.split(',').map(c => c.trim()).filter(Boolean));

      try {
        const avail = JSON.parse(String(r[F.availability] || '{}'));
        if (avail && typeof avail === 'object') setAvailability(avail);
      } catch {}

      try {
        const extras = JSON.parse(String(r[F.notes] || '{}'));
        if (extras.dob)            setDob(extras.dob);
        if (extras.shirtSize)      setShirtSize(extras.shirtSize);
        if (extras.dietary)        setDietary(extras.dietary);
        if (extras.ecRelationship) setEcRelationship(extras.ecRelationship);
        if (extras.vehicle)        setVehicle(extras.vehicle);
        if (extras.rolePref)       setRolePref(extras.rolePref);
        if (Array.isArray(extras.equipment)) setEquipment(extras.equipment);
        if (extras.languages) {
          const lang = extras.languages as string;
          if (LANGUAGE_OPTIONS.some(o => o.value === lang)) { setLanguage(lang); }
          else if (lang) { setLanguage('Other'); setLanguageOther(lang); }
        }
      } catch {}
    } catch (err) {
      console.warn('Could not pre-fill volunteer data:', err);
    } finally {
      setPrefillLoading(false);
    }
  }, [loadAssignments]);

  // ── Load events first, then restore session ───────────────

  useEffect(() => {
    (async () => {
      try {
        const [evtData, incData] = await Promise.all([
          fetchTable(TABLES.volunteerEvents, { limit: 200 }),
          fetchTable(TABLES.incidents, { limit: 200 }),
        ]);
        const FI = FIELDS.incidents;
        const FE = FIELDS.volunteerEvents;

        const incs: PublicIncident[] = (incData as Record<string, unknown>[]).map(r => ({
          id: Number(r.record_id) || Number(r.id) || 0,
          name: String(r[FI.name] ?? ''), status: String(r[FI.status] ?? ''),
          incidentType: String(r[FI.incidentType] ?? ''),
        })).filter(i => i.status === 'active' || i.status === 'monitoring');

        const activeIncIds = new Set(incs.map(i => String(i.id)));
        const evts: PublicEvent[] = (evtData as Record<string, unknown>[]).map(r => ({
          id: Number(r.record_id) || Number(r.id) || 0,
          incidentId: String(r[FE.incidentId] ?? ''), name: String(r[FE.eventName] ?? ''),
          type: 'other', status: String(r[FE.status] ?? ''),
          location: String(r[FE.location] ?? ''), address: '',
          startDate: String(r[FE.eventDate] ?? ''), endDate: '',
          targetVol: Number(r[FE.maxVolunteers]) || 0,
          checkInCode: String(r[FE.checkInCode] ?? ''), notes: String(r[FE.notes] ?? ''),
        })).filter(e => (e.status === 'planned' || e.status === 'active') && activeIncIds.has(e.incidentId));

        setIncidents(incs);
        setEvents(evts);

        // Now restore session (needs events for assignment lookup)
        const saved = loadSession();
        if (saved) {
          setSession(saved);
          prefillFromSession(saved, evts);
        }
      } catch (err) {
        console.error('Failed to load events:', err);
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived ───────────────────────────────────────────────

  const filteredEvents = useMemo(() => {
    if (incidentId) return events.filter(e => e.incidentId === incidentId);
    return events;
  }, [events, incidentId]);

  const eventsByIncident = useMemo(() => {
    const map = new Map<string, PublicEvent[]>();
    filteredEvents.forEach(e => {
      const arr = map.get(e.incidentId) || [];
      arr.push(e);
      map.set(e.incidentId, arr);
    });
    return map;
  }, [filteredEvents]);

  const selectedEvent = useMemo(
    () => events.find(e => e.id === selectedEventId) || null,
    [events, selectedEventId],
  );

  // ── Auth callbacks ─────────────────────────────────────────

  const handleAuth = useCallback((s: VolunteerSession) => {
    setSession(s);
    prefillFromSession(s, events);
  }, [prefillFromSession, events]);

  const handleRegister = useCallback((s: VolunteerSession) => {
    setSession(s);
    setFirstName(s.firstName || '');
    setLastName(s.lastName   || '');
    setEmail(s.email         || '');
    setStep(1);
  }, []);

  const handleSignOut = useCallback(() => {
    clearSession();
    setSession(null); setVolunteerRecordId(null);
    setRegisteredEventIds(new Set()); setMyAssignments([]);
    setFirstName(''); setLastName(''); setEmail(''); setPhone('');
    setAddress(''); setCity(''); setState(''); setZip('');
    setDob(''); setShirtSize(null); setDietary('');
    setEcName(''); setEcPhone(''); setEcRelationship('');
    setCerts([]); setSkills([]); setEquipment([]);
    setRolePref('both'); setVehicle('no');
    setLanguage(null); setLanguageOther('');
    setAvailability({}); setStep(0); setEventsTab('available');
  }, []);

  // ── Select event ──────────────────────────────────────────

  const handleSelectEvent = useCallback((ev: PublicEvent) => {
    setSelectedEventId(ev.id);
    if (session && volunteerRecordId) setStep(STEP_CONFIRM);
    else setStep(1);
  }, [session, volunteerRecordId]);

  // ── Availability ──────────────────────────────────────────

  const toggleAvail = (day: string, period: string) => {
    setAvailability(prev => {
      const d = day.toLowerCase();
      return { ...prev, [d]: { ...(prev[d] || {}), [period]: !(prev[d] || {})[period] } };
    });
  };
  const isAvail = (d: string, p: string) => !!(availability[d.toLowerCase()] || {})[p];

  // ── Full submit (new account profile or guest) ────────────

  const handleSubmit = useCallback(async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      notifications.show({ message: 'Name and email are required', color: 'var(--color-error)' }); return;
    }
    if (!waiverAccepted) {
      notifications.show({ message: 'You must accept the waiver to continue', color: 'var(--color-error)' }); return;
    }
    setSubmitting(true);
    try {
      const F = FIELDS.volunteers;
      const languageValue = language === 'Other' ? languageOther : (language ?? '');
      const extras = {
        dob, address, city, state, zip, shirtSize, dietary, ecRelationship,
        equipment, languages: languageValue, vehicle, rolePref,
        waiverAccepted: true, waiverDate: new Date().toISOString(), selfRegistered: true,
        ...(session ? { gabUserId: session.id } : {}),
      };

      const rid = await createRecord(TABLES.volunteers, [
        [F.firstName, firstName.trim()], [F.lastName, lastName.trim()],
        [F.email, email.trim()], [F.phone, phone.trim()],
        [F.org, ''], [F.rolePref, rolePref || 'both'],
        [F.certs, certs.join(', ')], [F.skills, skills.join(', ')],
        [F.status, 'probationary'], [F.availability, JSON.stringify(availability)],
      ]);
      await updateRecord(TABLES.volunteers, rid, [
        [F.ecName, ecName], [F.ecPhone, ecPhone],
        [F.notes, JSON.stringify(extras)], [F.registeredAt, new Date().toISOString()],
      ]);

      if (selectedEventId && selectedEvent) {
        const AF = FIELDS.volAssignments;
        await createRecord(TABLES.volAssignments, [
          [AF.volunteerId, String(rid)], [AF.eventId, String(selectedEventId)],
          [AF.volunteerName, `${firstName.trim()} ${lastName.trim()}`],
          [AF.role, rolePref || 'both'], [AF.status, 'confirmed'],
          [AF.notes, JSON.stringify({ eventName: selectedEvent.name, selfRegistered: true })],
        ]);
      }

      if (session) {
        // Authenticated new user: save record ID, reload assignments, go back to events
        setVolunteerRecordId(rid);
        if (selectedEventId) {
          setRegisteredEventIds(prev => new Set([...prev, selectedEventId]));
          setMyAssignments(prev => [...prev, {
            assignmentId: 0, eventId: selectedEventId,
            eventName: selectedEvent?.name || '', location: selectedEvent?.location || '',
            startDate: selectedEvent?.startDate || '', status: 'confirmed', role: rolePref || 'both',
          }]);
        }
        notifications.show({ message: 'Profile saved! You\'re all set.', color: '#03AC9E' });
        setSelectedEventId(null);
        setStep(0);
        if (myAssignments.length > 0 || selectedEventId) setEventsTab('mine');
      } else {
        // Guest: show success screen
        setSubmitted(true);
      }
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Registration failed', color: 'var(--color-error)' });
    } finally { setSubmitting(false); }
  }, [
    firstName, lastName, email, phone, dob, address, city, state, zip,
    shirtSize, dietary, ecName, ecPhone, ecRelationship, certs, skills,
    equipment, rolePref, vehicle, language, languageOther, availability,
    waiverAccepted, selectedEventId, selectedEvent, session, myAssignments.length,
  ]);

  // ── Un-register from an event ────────────────────────────

  const handleUnregister = useCallback(async (assignmentId: number, eventId: number) => {
    try {
      await deleteRecord(TABLES.volunteerAssignments, assignmentId);
      setMyAssignments(prev => prev.filter(a => a.assignmentId !== assignmentId));
      setRegisteredEventIds(prev => { const s = new Set(prev); s.delete(eventId); return s; });
      notifications.show({ message: 'You have been un-registered from the event.', color: '#C68700' });
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Could not cancel registration', color: 'var(--color-error)' });
    }
  }, []);

  // ── Quick confirm submit (returning user) ─────────────────

  const handleQuickSubmit = useCallback(async () => {
    if (!volunteerRecordId || !selectedEventId || !selectedEvent) return;
    setSubmitting(true);
    try {
      const AF = FIELDS.volAssignments;
      await createRecord(TABLES.volAssignments, [
        [AF.volunteerId, String(volunteerRecordId)],
        [AF.eventId, String(selectedEventId)],
        [AF.volunteerName, `${firstName.trim()} ${lastName.trim()}`],
        [AF.role, rolePref || 'both'], [AF.status, 'confirmed'],
        [AF.notes, JSON.stringify({ eventName: selectedEvent.name, selfRegistered: true })],
      ]);

      // Update local state immediately
      setRegisteredEventIds(prev => new Set([...prev, selectedEventId]));
      setMyAssignments(prev => [...prev, {
        assignmentId: 0, eventId: selectedEventId,
        eventName: selectedEvent.name, location: selectedEvent.location,
        startDate: selectedEvent.startDate, status: 'confirmed', role: rolePref || 'both',
      }]);

      notifications.show({ message: `You're signed up for ${selectedEvent.name}!`, color: '#03AC9E' });
      setSelectedEventId(null);
      setStep(0);
      setEventsTab('mine');
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Sign-up failed', color: 'var(--color-error)' });
    } finally { setSubmitting(false); }
  }, [volunteerRecordId, selectedEventId, selectedEvent, firstName, lastName, rolePref]);

  // ── Shared topbar ─────────────────────────────────────────

  const topbar = (
    <div className="pf-topbar">
      <img src="/cds/assets/logo/OpenGov-Logo-Blurple.svg" alt="OpenGov" className="pf-topbar-logo" />
      <div className="pf-topbar-divider" />
      <span className="pf-topbar-product">Emergency Management</span>
    </div>
  );

  // ── Loading ───────────────────────────────────────────────

  if (loading) {
    return <div className="pf-page">{topbar}<div className="pf-content pf-content--narrow"><div className="pf-card pf-state-wrap"><Loader size="lg" /></div></div></div>;
  }

  // ── Guest success screen ──────────────────────────────────

  if (submitted) {
    return (
      <div className="pf-page">{topbar}
        <div className="pf-content pf-content--narrow">
          <div className="pf-card pf-state-wrap">
            <div className="pf-state-icon pf-state-icon--success"><IconCheck size={30} /></div>
            <p className="pf-state-title">Registration Complete!</p>
            <p className="pf-state-sub">
              Thank you, {firstName}! You have been registered as a volunteer.
              {selectedEvent && <> You are signed up for <b>{selectedEvent.name}</b>.</>}
              {' '}Your status is &ldquo;probationary&rdquo; — an administrator will review your registration and reach out at <b>{email}</b>.
            </p>
          </div>
        </div>
        <p className="pf-footer">Powered by OpenGov Emergency Management</p>
      </div>
    );
  }

  // ── Quick Confirm ─────────────────────────────────────────

  if (step === STEP_CONFIRM && session && selectedEvent) {
    return (
      <div className="pf-page">{topbar}
        <div className="pf-content pf-content--narrow">
          <div className="pf-header-card">
            <div className="pf-header-icon"><IconCalendarEvent size={22} /></div>
            <div><p className="pf-header-title">Confirm Sign-Up</p><p className="pf-header-sub">Review and confirm your registration.</p></div>
          </div>

          <Paper p="md" radius="sm" withBorder mb="md" style={{ borderColor: '#03AC9E', backgroundColor: '#f0fffe' }}>
            <Text fw={700} size="md" mb={4}>{selectedEvent.name}</Text>
            {selectedEvent.location && (
              <Group gap={4} mb={2}><IconMapPin size={13} style={{ color: secondary[700] }} /><Text size="sm" c="dimmed">{selectedEvent.location}</Text></Group>
            )}
            {selectedEvent.startDate && <Text size="sm" c="dimmed">{selectedEvent.startDate}</Text>}
          </Paper>

          <Paper p="md" radius="sm" withBorder mb="md">
            <Group justify="space-between" mb="sm">
              <Text fw={600} size="sm">Your Information on File</Text>
              <Button variant="subtle" size="xs" color="gray" leftSection={<IconEdit size={12} />} onClick={() => setStep(1)}>Update</Button>
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <div><Text size="xs" c="dimmed">Name</Text><Text size="sm" fw={500}>{firstName} {lastName}</Text></div>
              <div><Text size="xs" c="dimmed">Email</Text><Text size="sm" fw={500}>{email}</Text></div>
              {phone && <div><Text size="xs" c="dimmed">Phone</Text><Text size="sm" fw={500}>{phone}</Text></div>}
              {city && state && <div><Text size="xs" c="dimmed">Location</Text><Text size="sm" fw={500}>{city}, {state}</Text></div>}
              {skills.length > 0 && <div style={{ gridColumn: '1 / -1' }}><Text size="xs" c="dimmed">Skills</Text><Text size="sm" fw={500}>{skills.join(', ')}</Text></div>}
            </SimpleGrid>
          </Paper>

          <Group justify="space-between">
            <Button variant="default" leftSection={<IconChevronLeft size={16} />}
              onClick={() => { setSelectedEventId(null); setStep(0); }}>Back to Events</Button>
            <Button color="#03AC9E" leftSection={<IconCheck size={16} />} loading={submitting} onClick={handleQuickSubmit}>Confirm Sign-Up</Button>
          </Group>
        </div>
        <p className="pf-footer">Powered by OpenGov Emergency Management</p>
      </div>
    );
  }

  // ── Event Browser (step 0) ────────────────────────────────

  if (step === 0) {
    const hasProfile = session && volunteerRecordId;

    return (
      <div className="pf-page">{topbar}
        <div className="pf-content" style={{ maxWidth: 860 }}>
          <div className="pf-header-card">
            <div className="pf-header-icon"><IconHeartHandshake size={22} /></div>
            <div>
              <p className="pf-header-title">Volunteer Sign-Up</p>
              <p className="pf-header-sub">Browse available opportunities and register to help your community.</p>
            </div>
          </div>

          <AuthPanel session={session} onAuth={handleAuth} onRegister={handleRegister} onGuest={() => setStep(1)} onSignOut={handleSignOut} />

          {prefillLoading && (
            <Group gap="xs" mb="sm"><Loader size="xs" /><Text size="xs" c="dimmed">Loading your saved information…</Text></Group>
          )}

          {hasProfile && !prefillLoading && (
            <Paper p="sm" radius="sm" mb="md" style={{ backgroundColor: '#f0fffe', border: '1px solid #99f6e4' }}>
              <Text size="sm" style={{ color: '#037a6e' }}>
                Your profile is on file — select an event below to sign up with one click.
              </Text>
            </Paper>
          )}

          {/* Tabs — only shown when signed in */}
          {session && (
            <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--color-neutral-200)', marginBottom: 16 }}>
              {[
                { key: 'available', label: 'Available Events', icon: <IconCalendarEvent size={14} /> },
                { key: 'mine',      label: `My Registrations${myAssignments.length ? ` (${myAssignments.length})` : ''}`, icon: <IconList size={14} /> },
              ].map(tab => (
                <button key={tab.key} type="button"
                  onClick={() => setEventsTab(tab.key as 'available' | 'mine')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer',
                    fontWeight: eventsTab === tab.key ? 600 : 400,
                    fontSize: 14, color: eventsTab === tab.key ? '#03AC9E' : '#6b7280',
                    borderBottom: eventsTab === tab.key ? '2px solid #03AC9E' : '2px solid transparent',
                    marginBottom: -2,
                  }}>
                  {tab.icon}{tab.label}
                </button>
              ))}
            </div>
          )}

          {/* My Registrations tab */}
          {eventsTab === 'mine' && session && (
            myAssignments.length === 0 ? (
              <div className="pf-card pf-state-wrap">
                <div className="pf-state-icon pf-state-icon--info"><IconAlertCircle size={30} /></div>
                <p className="pf-state-title">No Registrations Yet</p>
                <p className="pf-state-sub">Switch to Available Events to sign up for your first event.</p>
              </div>
            ) : (
              <div className="pf-card">
                <Stack gap="sm">
                  {myAssignments.map((a, i) => (
                    <Paper key={a.assignmentId || i} p="md" radius="sm" withBorder>
                      <Group justify="space-between" mb={4}>
                        <Text fw={600}>{a.eventName}</Text>
                        <Badge size="sm" style={{ backgroundColor: STATUS_COLORS[a.status] || '#939598', color: '#fff' }} tt="capitalize">
                          {a.status}
                        </Badge>
                      </Group>
                      {a.location && (
                        <Group gap={4} mb={2}><IconMapPin size={12} style={{ color: secondary[700] }} /><Text size="xs" c="dimmed">{a.location}</Text></Group>
                      )}
                      {a.startDate && <Text size="xs" c="dimmed">{a.startDate}</Text>}
                      {a.role && a.role !== 'both' && <Text size="xs" c="dimmed" mt={2}>Role: {a.role}</Text>}
                      {a.assignmentId > 0 && (a.status === 'confirmed' || a.status === 'pending') && (
                        <Button mt="xs" size="xs" variant="subtle" color="red" fullWidth
                          onClick={() => handleUnregister(a.assignmentId, a.eventId)}>
                          Cancel Registration
                        </Button>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </div>
            )
          )}

          {/* Available Events tab */}
          {eventsTab === 'available' && (
            filteredEvents.length === 0 ? (
              <div className="pf-card pf-state-wrap">
                <div className="pf-state-icon pf-state-icon--info"><IconAlertCircle size={30} /></div>
                <p className="pf-state-title">No Events Available</p>
                <p className="pf-state-sub">No volunteer events are available at this time. Please check back later.</p>
              </div>
            ) : (
              <div className="pf-card">
                <Stack gap="lg">
                  {Array.from(eventsByIncident.entries()).map(([incId, evts]) => {
                    const inc = incidents.find(i => String(i.id) === incId);
                    return (
                      <div key={incId}>
                        <Group gap="xs" mb="sm">
                          <Text fw={700} size="lg">{inc?.name || 'Incident'}</Text>
                          <Badge variant="light" color="var(--color-success)" tt="capitalize">{inc?.incidentType || ''}</Badge>
                        </Group>
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                          {evts.map(ev => {
                            const isRegistered = registeredEventIds.has(ev.id);
                            return (
                              <Paper key={ev.id} p="md" radius="sm" withBorder
                                style={isRegistered ? { backgroundColor: '#f0fffe', borderColor: '#99f6e4' } : undefined}>
                                <Group justify="space-between" mb="xs">
                                  <Text fw={600}>{ev.name}</Text>
                                  <Group gap={4}>
                                    {isRegistered && (
                                      <Badge size="xs" style={{ backgroundColor: '#03AC9E', color: '#fff' }}>
                                        Registered ✓
                                      </Badge>
                                    )}
                                    <Badge size="xs" color={TYPE_COLORS[ev.type] || '#939598'} tt="capitalize">{ev.type}</Badge>
                                  </Group>
                                </Group>
                                {ev.location && (
                                  <Group gap={4} mb={4}><IconMapPin size={12} style={{ color: secondary[700] }} /><Text size="xs" c="dimmed">{ev.location}</Text></Group>
                                )}
                                {ev.address && <Text size="xs" c="dimmed" mb={4}>{ev.address}</Text>}
                                <Text size="xs" c="dimmed" mb="sm">{ev.startDate || '?'} — {ev.endDate || '?'}</Text>

                                {isRegistered ? (
                                  <Button fullWidth variant="light" color="teal" size="sm" disabled
                                    leftSection={<IconCheck size={16} />}>
                                    Already Signed Up
                                  </Button>
                                ) : (
                                  <Button fullWidth color="#03AC9E" size="sm"
                                    leftSection={hasProfile ? <IconCheck size={16} /> : <IconCalendarEvent size={16} />}
                                    onClick={() => handleSelectEvent(ev)}>
                                    {hasProfile ? 'Sign Up — One Click' : 'Sign Up for This Event'}
                                  </Button>
                                )}
                              </Paper>
                            );
                          })}
                        </SimpleGrid>
                      </div>
                    );
                  })}
                  <Divider />
                  <Button variant="light" color="#03AC9E" size="md"
                    onClick={() => { setSelectedEventId(null); setStep(hasProfile ? STEP_CONFIRM : 1); }}>
                    Register Without Selecting an Event
                  </Button>
                </Stack>
              </div>
            )
          )}
        </div>
        <p className="pf-footer">Powered by OpenGov Emergency Management</p>
      </div>
    );
  }

  // ── Registration Form (steps 1-5) ─────────────────────────

  const isNewAccount = !!session && !volunteerRecordId;

  return (
    <div className="pf-page">{topbar}
      <div className="pf-content">
        <div className="pf-header-card">
          <div className="pf-header-icon"><IconHeartHandshake size={22} /></div>
          <div>
            <p className="pf-header-title">{isNewAccount ? 'Set Up Your Volunteer Profile' : 'Volunteer Registration'}</p>
            {isNewAccount
              ? <p className="pf-header-sub">Complete your profile once — future sign-ups will only take a click.</p>
              : selectedEvent
                ? <p className="pf-header-sub">Signing up for: <b>{selectedEvent.name}</b></p>
                : <p className="pf-header-sub">Complete the steps below to register as a volunteer.</p>
            }
          </div>
          {session && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, color: '#03AC9E', fontSize: 13 }}>
              <IconUser size={14} /><span>{session.firstName} {session.lastName}</span>
            </div>
          )}
        </div>

        <div className="pf-card" style={{ marginBottom: 16 }}>
          <Stepper active={step - 1} size="sm" mb="xl" color="#03AC9E">
            <Stepper.Step label="Personal" /><Stepper.Step label="Emergency" />
            <Stepper.Step label="Skills" /><Stepper.Step label="Availability" />
            <Stepper.Step label="Waiver" />
          </Stepper>

          {step === 1 && (
            <Paper p="md" radius="sm" withBorder>
              <Stack gap="sm">
                <Text fw={600}>Personal Information</Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <TextInput label="First Name" required value={firstName} autoComplete="given-name" leftSection={<IconUser size={14} />} onChange={e => setFirstName(e.target.value)} />
                  <TextInput label="Last Name" required value={lastName} autoComplete="family-name" onChange={e => setLastName(e.target.value)} />
                </SimpleGrid>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <TextInput label="Email" required value={email} type="email" autoComplete="email" leftSection={<IconMail size={14} />} onChange={e => setEmail(e.target.value)} />
                  <TextInput label="Phone" value={phone} type="tel" autoComplete="tel" placeholder="(555) 555-5555" leftSection={<IconPhone size={14} />} onChange={e => setPhone(formatPhone(e.target.value))} />
                </SimpleGrid>
                <TextInput label="Street Address" value={address} autoComplete="street-address" leftSection={<IconMapPin size={14} />} onChange={e => setAddress(e.target.value)} />
                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                  <TextInput label="City" value={city} autoComplete="address-level2" onChange={e => setCity(e.target.value)} />
                  <TextInput label="State" value={state} autoComplete="address-level1" onChange={e => setState(e.target.value)} />
                  <TextInput label="ZIP" value={zip} autoComplete="postal-code" onChange={e => setZip(e.target.value)} />
                </SimpleGrid>
                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                  <TextInput label="Date of Birth" type="date" value={dob} onChange={e => setDob(e.target.value)} />
                  <Select label="T-Shirt Size" data={SHIRT_SIZES} value={shirtSize} onChange={setShirtSize} clearable />
                  <TextInput label="Dietary Needs" placeholder="Allergies, restrictions..." value={dietary} onChange={e => setDietary(e.target.value)} />
                </SimpleGrid>
              </Stack>
            </Paper>
          )}

          {step === 2 && (
            <Paper p="md" radius="sm" withBorder>
              <Stack gap="sm">
                <Text fw={600}>Emergency Contact</Text>
                <TextInput label="Contact Name" value={ecName} autoComplete="off" onChange={e => setEcName(e.target.value)} />
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <TextInput label="Phone" value={ecPhone} type="tel" autoComplete="off" placeholder="(555) 555-5555" leftSection={<IconPhone size={14} />} onChange={e => setEcPhone(formatPhone(e.target.value))} />
                  <TextInput label="Relationship" value={ecRelationship} autoComplete="off" onChange={e => setEcRelationship(e.target.value)} />
                </SimpleGrid>
              </Stack>
            </Paper>
          )}

          {step === 3 && (
            <Paper p="md" radius="sm" withBorder>
              <Stack gap="md">
                <div>
                  <Text fw={600} mb="xs">Certifications</Text>
                  <Group gap="xs" wrap="wrap">
                    {CERT_OPTIONS.map(c => <Checkbox key={c} label={c} size="xs" checked={certs.includes(c)} onChange={e => { if (e.currentTarget.checked) setCerts(p => [...p, c]); else setCerts(p => p.filter(x => x !== c)); }} />)}
                  </Group>
                </div>
                <div>
                  <Text fw={600} mb="xs">Skills</Text>
                  <Group gap="xs" wrap="wrap">
                    {SKILL_OPTIONS.map(s => <Checkbox key={s} label={s} size="xs" checked={skills.includes(s)} onChange={e => { if (e.currentTarget.checked) setSkills(p => [...p, s]); else setSkills(p => p.filter(x => x !== s)); }} />)}
                  </Group>
                </div>
                <div>
                  <Text fw={600} mb="xs">Equipment You Can Bring</Text>
                  <Group gap="xs" wrap="wrap">
                    {EQUIPMENT_OPTIONS.map(eq => <Checkbox key={eq} label={eq} size="xs" checked={equipment.includes(eq)} onChange={e => { if (e.currentTarget.checked) setEquipment(p => [...p, eq]); else setEquipment(p => p.filter(x => x !== eq)); }} />)}
                  </Group>
                </div>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <Select label="Role Preference" data={ROLE_OPTIONS} value={rolePref} onChange={setRolePref} />
                  <Select label="Vehicle" data={VEHICLE_OPTIONS} value={vehicle} onChange={setVehicle} />
                </SimpleGrid>
                <div>
                  <Select label="Primary Language Spoken" data={LANGUAGE_OPTIONS} value={language} onChange={v => { setLanguage(v); setLanguageOther(''); }} clearable placeholder="Select a language..." />
                  {language === 'Other' && <TextInput mt="xs" label="Please specify" placeholder="Language spoken..." value={languageOther} onChange={e => setLanguageOther(e.target.value)} />}
                </div>
              </Stack>
            </Paper>
          )}

          {step === 4 && (
            <Paper p="md" radius="sm" withBorder>
              <Stack gap="sm">
                <Text fw={600}>Availability</Text>
                <Text size="xs" c="dimmed">Click cells to mark when you are available to volunteer.</Text>
                <Paper p="xs" radius="sm" withBorder>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead><tr>
                      <th style={{ padding: 8, width: 60 }} />
                      {PERIODS.map(p => <th key={p.key} style={{ padding: 8, textAlign: 'center', fontSize: 12 }}>{p.label}</th>)}
                    </tr></thead>
                    <tbody>
                      {DAYS.map(day => (
                        <tr key={day}>
                          <td style={{ padding: 8, fontWeight: 600 }}>{day}</td>
                          {PERIODS.map(p => (
                            <td key={p.key} onClick={() => toggleAvail(day, p.key)} style={{
                              padding: 8, textAlign: 'center', cursor: 'pointer',
                              backgroundColor: isAvail(day, p.key) ? '#059669' : 'transparent',
                              color: isAvail(day, p.key) ? '#fff' : 'inherit',
                              border: '1px solid #e2e8f0', borderRadius: 4,
                            }}>{isAvail(day, p.key) ? 'Yes' : '—'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Paper>
              </Stack>
            </Paper>
          )}

          {step === 5 && (
            <Paper p="md" radius="sm" withBorder>
              <Stack gap="sm">
                <Text fw={600}><IconShieldCheck size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />Volunteer Liability Waiver</Text>
                <ScrollArea h={250} type="always">
                  <Paper p="md" radius="sm" style={{ backgroundColor: '#F8F8F8' }}>
                    <Text size="sm" style={{ whiteSpace: 'pre-line' }}>{WAIVER_TEXT}</Text>
                  </Paper>
                </ScrollArea>
                <Checkbox label="I have read and agree to the Volunteer Liability Waiver & Release" checked={waiverAccepted} onChange={e => setWaiverAccepted(e.currentTarget.checked)} color="#03AC9E" />
              </Stack>
            </Paper>
          )}

          <Group justify="space-between" mt="lg">
            <Button variant="default" leftSection={<IconChevronLeft size={16} />}
              onClick={() => { if (step === 1) setStep(0); else setStep(s => s - 1); }}>
              {step === 1 ? 'Back to Events' : 'Previous'}
            </Button>
            {step < 5 ? (
              <Button color="#03AC9E" rightSection={<IconChevronRight size={16} />}
                onClick={() => {
                  if (step === 1 && (!firstName.trim() || !lastName.trim() || !email.trim())) {
                    notifications.show({ message: 'First name, last name, and email are required', color: 'var(--color-error)' }); return;
                  }
                  setStep(s => s + 1);
                }}>Next</Button>
            ) : (
              <Button color="#03AC9E" leftSection={<IconSend size={16} />}
                onClick={handleSubmit} loading={submitting} disabled={!waiverAccepted}>
                {isNewAccount ? 'Save Profile & Complete Sign-Up' : 'Submit Registration'}
              </Button>
            )}
          </Group>
        </div>
      </div>
      <p className="pf-footer">Powered by OpenGov Emergency Management</p>
    </div>
  );
}
